import { Injectable } from "@angular/core";
import Hotjar from "@hotjar/browser";
import posthog from "posthog-js";
import { AuthService } from "../auth";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class AnalyticsService {

  constructor(private readonly authService: AuthService) { }

  identifyUser() {
    if (environment.analytics.identify) {
      if (window.rudderanalytics) {
        window.rudderanalytics.identify(this.authService.getLoggedUser()?.ecotx_sid);
      }
      if (environment.hotjar.enabled && environment.hotjar.identify) {
        Hotjar.identify(this.authService.getLoggedUser().ecotx_sid, {
          username: this.authService.getLoggedUser().name,
          email: this.authService.getLoggedUser().email,
        });
      }

      if (environment.postHog.enabled) {
        const user = this.authService.currentUserValue;
        posthog.identify(this.authService.getLoggedUser()?.ecotx_sid, {
          email: this.authService.getLoggedUser()?.email,
          name: `${user?.firstName} ${user?.lastName}`,
          ...this.buildOrgProperties(),
        });
        this.registerOrgProperties();
      }
    }
  }

  /**
   * A lista de organizações chega pelo /me e pode não estar no currentUserValue
   * no instante do identify (e muda quando o usuário troca de organização).
   * Ler uma única vez deixava active_org_name vazio em todos os eventos, então
   * acompanhamos o currentUser$ e re-registramos sempre que o nome mudar.
   */
  private orgSubscribed = false;
  private lastOrgKey: string;

  private buildOrgProperties() {
    const user = this.authService.currentUserValue;
    const activeOrgId =
      user?.activeOrgId || this.authService.getLoggedUser()?.active_org_id;
    const activeOrg = user?.organizations?.find((org) => org.id === activeOrgId);
    return {
      active_org_id: activeOrgId,
      active_org_name: activeOrg?.tradingName || activeOrg?.name,
    };
  }

  private registerOrgProperties() {
    // Super properties: carimbam a organização (id + nome legível) em todos
    // os eventos da sessão, permitindo segmentar qualquer métrica por
    // cliente no plano free (group analytics é add-on pago).
    const apply = () => {
      const props = this.buildOrgProperties();
      const key = `${props.active_org_id}|${props.active_org_name}`;
      if (props.active_org_id && key !== this.lastOrgKey) {
        this.lastOrgKey = key;
        posthog.register(props);
      }
    };

    apply();

    if (!this.orgSubscribed) {
      this.orgSubscribed = true;
      this.authService.currentUser$.subscribe(() => apply());
    }
  }

  trackLogoutMultisession() {
    if (environment.analytics.trackLogout) {
      const eventProperties = {
        u: this.authService.getLoggedUser().ecotx_sid,
        t: Date.now(),
        s: "logout",
      };
      window.rudderanalytics.track("lms", eventProperties);
    }
  }

  trackWarnMultisession() {
    if (environment.analytics.trackLogout) {
      const eventProperties = {
        u: this.authService.getLoggedUser().ecotx_sid,
        t: Date.now(),
        s: "warn",
      };
      window.rudderanalytics.track("lms", eventProperties);
    }
  }

  trackDashboardAccess(dashboardId: string) {
    if (environment.analytics.trackDashboardAccess && window.rudderanalytics) {
      const eventProperties = {
        u: this.authService.getLoggedUser()?.ecotx_sid,
        d: dashboardId,
      };
      window.rudderanalytics.track("uda", eventProperties);
    }

    if (environment.postHog.enabled) {
      posthog.capture("powerbi_dashboard_accessed", {
        dashboard_id: dashboardId,
      });
    }
  }

  trackUserPageAccess(pageName: string) {
    // Only track if identification is enabled or specifically if page access tracking is desired.
    // Assuming we should always track if analytics is generally enabled/configured.
    // Using environment.analytics.identify as a proxy for 'analytics enabled' or just check rudderanalytics presence.
    // Given other methods check specific flags, I'll assume we can track this always if rudderanalytics exists,
    // or checks environment.analytics.identify if that's the main switch.
    // Actually, looking at trackDashboardAccess, it has a specific flag.
    // User didn't ask to add a flag, but implies "ao acessar qualquer página".
    // I will check if window.rudderanalytics is available.

    if (window.rudderanalytics) {
      const eventProperties = {
        u: this.authService.getLoggedUser()?.ecotx_sid,
        p: pageName,
      };
      window.rudderanalytics.track("upa", eventProperties);
    }

    if (environment.postHog.enabled) {
      posthog.capture("page_accessed", { page: pageName });
    }
  }

  trackLabsAppAccess(appKey: string, granted: boolean, appName?: string) {
    if (environment.analytics.trackLabsAppAccess && window.rudderanalytics) {
      const eventProperties = {
        u: this.authService.getLoggedUser()?.ecotx_sid,
        a: appKey,
        g: granted,
      };
      window.rudderanalytics.track("laa", eventProperties);
    }

    if (environment.postHog.enabled) {
      // "granted: false" = usuário clicou num produto que não tem acesso — sinal de upsell.
      posthog.capture("labs_card_clicked", {
        card_key: appKey,
        card_name: appName,
        allowed: granted,
      });
    }
  }

}
